import { supabase } from '../lib/supabase';

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
};

const toSnakeCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, (letter) => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
};

export const categoryService = {
  // Get all active categories
  async getAll() {
    const { data, error } = await supabase
      ?.from('service_categories')
      ?.select('*')
      ?.eq('is_active', true)
      ?.order('display_order');
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Get category by slug
  async getBySlug(slug) {
    const { data, error } = await supabase
      ?.from('service_categories')
      ?.select('*')
      ?.eq('slug', slug)
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Get subcategories by category
  async getSubcategories(categoryId) {
    const { data, error } = await supabase
      ?.from('service_subcategories')
      ?.select('*')
      ?.eq('category_id', categoryId)
      ?.eq('is_active', true)
      ?.order('display_order');
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Admin: Get all categories (including inactive)
  async getAllForAdmin() {
    const { data, error } = await supabase
      ?.from('service_categories')
      ?.select('*')
      ?.order('display_order');
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Admin: Get all subcategories
  async getAllSubcategoriesForAdmin(categoryId) {
    let query = supabase?.from('service_subcategories')?.select('*')?.order('display_order');
    if (categoryId) query = query?.eq('category_id', categoryId);
    const { data, error } = await query;
    if (error) throw error;
    return toCamelCase(data || []);
  },

  // Admin: Create category
  async createCategory(categoryData) {
    const snakeData = toSnakeCase(categoryData);
    const { data, error } = await supabase
      ?.from('service_categories')
      ?.insert([snakeData])
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Admin: Update category
  async updateCategory(id, categoryData) {
    const snakeData = toSnakeCase({ ...categoryData, updatedAt: new Date()?.toISOString() });
    const { data, error } = await supabase
      ?.from('service_categories')
      ?.update(snakeData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Admin: Delete category
  async deleteCategory(id) {
    const { error } = await supabase
      ?.from('service_categories')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
  },

  // Admin: Create subcategory
  async createSubcategory(subcategoryData) {
    const snakeData = toSnakeCase(subcategoryData);
    const { data, error } = await supabase
      ?.from('service_subcategories')
      ?.insert([snakeData])
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Admin: Update subcategory
  async updateSubcategory(id, subcategoryData) {
    const snakeData = toSnakeCase({ ...subcategoryData, updatedAt: new Date()?.toISOString() });
    const { data, error } = await supabase
      ?.from('service_subcategories')
      ?.update(snakeData)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return toCamelCase(data);
  },

  // Admin: Delete subcategory
  async deleteSubcategory(id) {
    const { error } = await supabase
      ?.from('service_subcategories')
      ?.delete()
      ?.eq('id', id);
    if (error) throw error;
  },
};